import { ChevronLeft } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { format } from 'date-fns'
import { enqueueSnackbar } from 'notistack'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useLocationContext } from '@providers/location'
import { useSessionContext } from '@providers/session'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locale'
import { getHasSharedCRM } from '@shared/locations'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { ButtonActions } from 'components-ui/src/button_actions/button_actions'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { Hooks, Services } from 'service-api'
import { AgreementClientDto, AgreementCreateInput, ContractClientDto } from 'service-api/src/types/api'

const MINIMUM_SIGNATURE_LENGTH = 2

export const Contracts = () => {
  const { accountName: locationName = '', contractId } = useParams()
  const { location } = useLocationContext()
  const hasSharedCRM = getHasSharedCRM(location)
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const [isLoading, setIsLoading] = useState(true)
  const [signature, setSignature] = useState('')
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [isContractSigned, contractSigned] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const [contract, setContract] = useState<ContractClientDto | undefined>(undefined)
  const hasSignatureMinLength = signature.length >= MINIMUM_SIGNATURE_LENGTH
  const lupId = sessionCtx.state.user?.lupId || ''
  const lup = Hooks.useClientLocationUserProfile(locationName, lupId, ['agreements.contract'])
  const [agreement, setAgreement] = useState<AgreementClientDto | undefined>(undefined)

  useEffect(() => {
    if (lup.isSuccess) {
      const agreement = lup.data?.agreements?.find((agreement) => agreement.contractId === contractId)
      setAgreement(agreement)
    }
  }, [contractId, lup.data?.agreements, lup.isSuccess])

  const onUpdateSignature = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target
    setSignature(value)
  }

  const onClearSignature = async () => {
    setSignature('')
    setAcceptTerms(false)
  }

  const onAcceptTerms = (ev: ChangeEvent<HTMLInputElement>) => {
    setAcceptTerms(ev.target.checked)
  }

  const submitSignature = async () => {
    setIsSubmitting(true)
    try {
      await Services.ClientAgreementsService.sign(contract?.location?.name || locationName, contract?.id || '', {
        customerName: signature,
        locationUserProfileId: lupId,
      } as AgreementCreateInput)
      contractSigned(true)
      enqueueSnackbar(t('profile.update.success'), { variant: 'success' })
    } catch (e) {
      captureError(e as Error)
      enqueueSnackbar(buildErrorMessage(e), { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const fetchContract = useCallback(async () => {
    try {
      const contract = await Services.ClientContractsService.getContract(locationName, contractId || '', {
        hasSharedCRM: !!hasSharedCRM,
      })
      if (!contract) {
        throw new Error('Contract not found')
      }

      setContract(contract)
    } catch (error) {
      enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
      captureError(error as Error)
    } finally {
      setIsLoading(false)
    }
  }, [contractId, hasSharedCRM, locationName])

  useEffect(() => {
    if (contractId && hasSharedCRM !== null) {
      fetchContract()
    }
  }, [fetchContract, contractId, hasSharedCRM])

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        variant="text"
        color="primary"
        size="large"
        onClick={() => navigateRoute('profileAgreements', {})}
        startIcon={<ChevronLeft />}
      >
        {t('profile.contracts.backToAgreements')}
      </Button>
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={200} />
      ) : (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h5" pl={isBelowMD ? 2 : 0}>
            {contract?.displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {agreement
              ? t('profile.agreements.signedAt', {
                  date: format(new Date(agreement.updatedAt!), 'M/d/yyyy'),
                  name: agreement.customerName || '',
                })
              : t('serviceAgreement.lead')}
          </Typography>
          <Box dangerouslySetInnerHTML={{ __html: contract?.description || '' }} sx={{ padding: '16px 24px' }} />
          {agreement ? null : (
            <Box px={4} py={2} sx={{ backgroundColor: theme.palette.background.default, maxWidth: '500px' }}>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox disabled={isContractSigned} checked={acceptTerms} onChange={onAcceptTerms} />}
                  label={t('serviceAgreement.form.accept')}
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold' } }}
                />
                <Spacer height={4} />
                <TextField
                  label="Signature"
                  value={signature}
                  disabled={isContractSigned}
                  onChange={onUpdateSignature}
                  helperText={t('serviceAgreement.form.sign.help')}
                  inputProps={{ disabled: isContractSigned }}
                  sx={{ marginBottom: 0, '& .MuiFormHelperText-root': { height: 'auto' } }}
                />
              </FormGroup>
              <Spacer height={2} />
              <ButtonActions
                invertButtons
                actionEnabled={!isSubmitting && hasSignatureMinLength && acceptTerms && !isContractSigned}
                onActionLabel={t('global.label.submit')}
                onAction={submitSignature}
                onCancel={!isContractSigned && (hasSignatureMinLength || acceptTerms) ? onClearSignature : undefined}
                onCancelLabel={t('global.label.clearAllChanges')}
                cancelEnabled={!isSubmitting && !isContractSigned}
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  )
}
